import { useEffect, useMemo } from 'react';
import { ProfileResponse, useLazyGetProfilesByUserIdQuery } from '../../store/backendApi';

interface UseTripOwnerProfileReturn {
  ownerProfile: ProfileResponse | undefined;
  isOwner: boolean;
  isLoadingProfile: boolean;
}

/**
 * Hook to load trip owner profile and check ownership.
 * Platform-agnostic: Works in both React and React Native.
 *
 * @param ownerId - The trip owner's user ID
 * @param currentUserId - The currently authenticated user's ID
 * @returns Owner profile data, ownership status, and loading state
 */
export const useTripOwnerProfile = (
  ownerId?: string,
  currentUserId?: string,
): UseTripOwnerProfileReturn => {
  const [loadProfile, { data: ownerProfile, isLoading: isLoadingProfile }] =
    useLazyGetProfilesByUserIdQuery();

  // Load profile when ownerId is available
  useEffect(() => {
    if (!ownerId) {
      return;
    }
    loadProfile(ownerId);
  }, [ownerId, loadProfile]);

  // Check if current user is the owner
  const isOwner = useMemo(
    () => !!ownerId && !!currentUserId && ownerId === currentUserId,
    [ownerId, currentUserId],
  );

  return {
    ownerProfile,
    isOwner,
    isLoadingProfile,
  };
};
